import { Avatar, Box, Group, Stack, Text } from '@mantine/core';
import { MChatConversation } from '@/src/modules/chat/models/chat-conversation';
import {
  formatDisplayShortTime,
  formatNumericDate,
} from '@/src/shared/utils/datetime';
import { ChatConversationLastMessage } from './chat-conversation-last-message';

interface IChatConversationTileProps {
  size?: 'sm' | 'md';
  overrideName?: string;
  avatar?: React.ReactNode;
  conversation: MChatConversation;
  overrideIntelocutorName?: string;
  onClick?: () => void;
  isSelected?: boolean;
  showLastMessage?: boolean;
}

const MAX_UNREAD_MESSAGE_COUNT = 9;

export function ChatConversationTile({
  size = 'md',
  avatar,
  overrideName,
  overrideIntelocutorName,
  conversation,
  onClick,
  isSelected = false,
  showLastMessage = true,
}: IChatConversationTileProps) {
  const isNormalSize = size === 'md';
  const recipientName = overrideName ?? conversation.props.recipient.props.name;
  const unreadMessageCountLabel =
    conversation.props.unreadMessageCount > MAX_UNREAD_MESSAGE_COUNT
      ? `${MAX_UNREAD_MESSAGE_COUNT}+`
      : conversation.props.unreadMessageCount;

  let timestampLabel: string | null = null;
  if (conversation.hasLastMessage) {
    if (conversation.props.lastMessage?.isSentToday) {
      timestampLabel = formatDisplayShortTime(
        conversation.props.lastMessage.props.sentAt
      );
    } else {
      timestampLabel = formatNumericDate(
        conversation.props.lastMessage!.props.sentAt
      );
    }
  }

  return (
    <Box
      p={isNormalSize ? 'sm' : '0.5rem'}
      bg={isSelected ? 'gray.1' : undefined}
      onClick={onClick}
      display="table"
      w="100%"
      bdrs="md"
      className="table-fixed cursor-pointer overflow-hidden"
    >
      <Box display="table-row">
        <Box
          display="table-cell"
          w={isNormalSize ? '3.5rem' : '2.8rem'}
          className="align-top"
        >
          {!avatar ? (
            <Avatar
              name={conversation.props.recipient.props.name}
              size={isNormalSize ? '2.7rem' : '2.1rem'}
              color="initials"
              alt={conversation.props.recipient.props.name}
            />
          ) : (
            avatar
          )}
        </Box>

        {showLastMessage && conversation.hasLastMessage ? (
          <Box display="table-cell">
            <Stack gap={0}>
              <Group justify="space-between" wrap="nowrap">
                <Text fw={600} fz="sm" truncate="end">
                  {recipientName}
                </Text>

                <Text fz="xs" c="dimmed" className="shrink-0">
                  {timestampLabel}
                </Text>
              </Group>

              <Group justify="space-between" wrap="nowrap">
                <ChatConversationLastMessage
                  message={conversation.props.lastMessage!}
                  overrideInteloctorName={overrideIntelocutorName}
                />

                {conversation.hasUnreadMessages && (
                  <Text size="xs" c="white" bg="brand" px={7} py={1} bdrs={999}>
                    {unreadMessageCountLabel}
                  </Text>
                )}
              </Group>
            </Stack>
          </Box>
        ) : (
          <Text
            fw={600}
            fz={isNormalSize ? 'sm' : 'xs'}
            truncate="end"
            display="table-cell"
            className="align-middle"
          >
            {recipientName}
          </Text>
        )}
      </Box>
    </Box>
  );
}
